import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateTechPartner, useTechPartnerProductOptions } from "@/hooks/use-tech-partners";

const createSchema = z.object({
  product: z.string().min(1, "Product is required"),
  partnerName: z
    .string()
    .min(1, "Tech partner name is required")
    .max(100, "Tech partner name must be 100 characters or fewer"),
});

type CreateValues = z.infer<typeof createSchema>;

interface CreateTechPartnerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  takenProducts: string[];
}

export function CreateTechPartnerDialog({
  open,
  onOpenChange,
  takenProducts,
}: CreateTechPartnerDialogProps) {
  const { data: productOptions = [], isLoading: isLoadingProducts } =
    useTechPartnerProductOptions();
  const create = useCreateTechPartner();

  const takenSet = new Set(takenProducts);
  const availableProducts = productOptions.filter((product) => !takenSet.has(product));

  const form = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: { product: "", partnerName: "" },
  });

  function onSubmit(values: CreateValues) {
    create.mutate(values, {
      onSuccess: () => {
        form.reset();
        onOpenChange(false);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Pairing</DialogTitle>
          <DialogDescription>Pair a product with a tech partner.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="product"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Product</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue
                          placeholder={isLoadingProducts ? "Loading products…" : "Select a product"}
                        />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {availableProducts.map((product) => (
                        <SelectItem key={product} value={product}>
                          {product}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="partnerName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tech Partner</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. Jordan Lee" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? "Creating..." : "Add Pairing"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
